/** Fallback when `ipuUnitCost` system config has not loaded. Average n2-highmem-8 SUD monthly price. */
export const DEFAULT_COST_PER_IPU_USD = 371.62;
export const IPU_MACHINE_TYPE = 'n2-highmem-8';
export const IPU_VCPU = 8;
export const IPU_MEMORY_GB = 64;

type IpuValue = number | string | null | undefined;

export type IpuUnitCostConfig = {
  default?: number;
  byRegion?: Record<string, number>;
  /** @deprecated legacy float payload */
  value?: number;
};

type SizeLike = {
  ipu?: IpuValue;
  cpu?: IpuValue;
  memoryLimit?: IpuValue;
} | null;

function toIpu(value: IpuValue): number {
  const ipu = Number(value);
  return Number.isFinite(ipu) ? ipu : 0;
}

function toPositiveNumber(value: IpuValue): number | null {
  const cost = Number(value);
  return Number.isFinite(cost) && cost > 0 ? cost : null;
}

function regionLookupKeys(region?: string | null): string[] {
  if (!region) return [];
  const keys = [region.trim()];
  const zoneMatch = keys[0].match(/^(.+)-[a-z]$/i);
  if (zoneMatch) keys.push(zoneMatch[1]);
  return keys;
}

export function parseIpuUnitCost(raw: unknown): IpuUnitCostConfig {
  const byRegion: Record<string, number> = {};
  let defaultCost = DEFAULT_COST_PER_IPU_USD;

  if (raw == null || raw === '') {
    return { default: defaultCost, byRegion };
  }

  if (typeof raw === 'number') {
    return { default: toPositiveNumber(raw) || defaultCost, byRegion };
  }

  let parsed: Record<string, unknown> | null = null;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    parsed = raw as Record<string, unknown>;
  } else {
    const text = String(raw).trim();
    if (!text.startsWith('{')) {
      return { default: toPositiveNumber(text) || defaultCost, byRegion };
    }
    try {
      parsed = JSON.parse(text);
    } catch {
      return { default: defaultCost, byRegion };
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { default: defaultCost, byRegion };
  }

  if (parsed.byRegion && typeof parsed.byRegion === 'object') {
    for (const [key, value] of Object.entries(
      parsed.byRegion as Record<string, unknown>,
    )) {
      const cost = toPositiveNumber(value as IpuValue);
      if (cost) byRegion[key] = cost;
    }
    const fromDefault = toPositiveNumber(parsed.default as IpuValue);
    const fromValue = toPositiveNumber(parsed.value as IpuValue);
    return { default: fromDefault || fromValue || defaultCost, byRegion };
  }

  for (const [key, value] of Object.entries(parsed)) {
    const cost = toPositiveNumber(value as IpuValue);
    if (!cost) continue;
    if (key === 'default') defaultCost = cost;
    else if (key !== 'byRegion' && key !== 'value') byRegion[key] = cost;
  }

  return { default: defaultCost, byRegion };
}

export function resolveCostPerIpuUsd(
  config?: IpuUnitCostConfig | number | string | null,
  region?: string | null,
): number {
  const parsed =
    typeof config === 'object' &&
    config &&
    ('default' in config || 'byRegion' in config)
      ? config
      : parseIpuUnitCost(config);

  const byRegion = parsed.byRegion ?? {};
  for (const key of regionLookupKeys(region)) {
    const cost = toPositiveNumber(byRegion[key]);
    if (cost) return cost;
  }

  return (
    toPositiveNumber(parsed.default) ||
    toPositiveNumber(parsed.value) ||
    DEFAULT_COST_PER_IPU_USD
  );
}

export function computeGenericIpu(cpu: number, memoryLimit: number): number {
  return (cpu * 0.5) / IPU_VCPU + (memoryLimit * 0.5) / IPU_MEMORY_GB;
}

export function estimateMonthlyCostUsd(
  ipu: IpuValue,
  costPerIpuUsd: IpuValue = DEFAULT_COST_PER_IPU_USD,
): number {
  return toIpu(ipu) * resolveCostPerIpuUsd(costPerIpuUsd);
}

export function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatEstimatedCost(
  ipu: IpuValue,
  costPerIpuUsd: IpuValue = DEFAULT_COST_PER_IPU_USD,
): string {
  return `~${formatUsd(estimateMonthlyCostUsd(ipu, costPerIpuUsd))}`;
}

export function formatEstimatedMonthlyCost(
  ipu: IpuValue,
  costPerIpuUsd: IpuValue = DEFAULT_COST_PER_IPU_USD,
): string {
  return `${formatEstimatedCost(ipu, costPerIpuUsd)}/mo`;
}

export function formatIpuWithEstimatedCost(
  ipu: IpuValue,
  ipuLabel?: string,
  costPerIpuUsd: IpuValue = DEFAULT_COST_PER_IPU_USD,
): string {
  const label = ipuLabel ?? `${toIpu(ipu)} IPU`;
  return `${label} · ${formatEstimatedCost(ipu, costPerIpuUsd)}`;
}

export function ipuChartTotalSuffix(
  totalIpu: number,
  showCost: boolean,
  costPerIpuUsd: IpuValue = DEFAULT_COST_PER_IPU_USD,
): string {
  return showCost
    ? `IPU · ${formatEstimatedCost(totalIpu, costPerIpuUsd)}`
    : 'IPU';
}

export function resolveSizeIpu(size?: SizeLike): number {
  if (!size) return 0;
  if (size.ipu != null && size.ipu !== '') {
    return toIpu(size.ipu);
  }
  if (size.cpu != null && size.memoryLimit != null) {
    return computeGenericIpu(Number(size.cpu), Number(size.memoryLimit));
  }
  return 0;
}

export function getClusterTotalIpu({
  analyticsIpu = 0,
  loaderIpu = 0,
  analyticsNodes,
  loaderNodes,
}: {
  analyticsIpu?: IpuValue;
  loaderIpu?: IpuValue;
  analyticsNodes?: IpuValue;
  loaderNodes?: IpuValue;
}): number {
  const analyticsCount = Math.max(Number(analyticsNodes) || 1, 0);
  const loaderCount = Math.max(Number(loaderNodes) || 1, 0);
  return toIpu(analyticsIpu) * analyticsCount + toIpu(loaderIpu) * loaderCount;
}
